'use client';

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowLeft, CheckCircle, Eye, AlertTriangle, Target } from "lucide-react";
import { useParams } from "next/navigation";

const visionQCData = {
  title: "Computer Vision for Quality Control",
  subtitle: "Enhancing manufacturing precision with AI-driven visual inspection.",
  headerGradient: "bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600",
  accentColor: "text-blue-600",
  buttonColor: "bg-blue-600 hover:bg-blue-700",
  challenge: "Manufacturers often face challenges in maintaining consistent quality, reducing defects, and optimizing inspection processes, which can be labor-intensive and prone to human error.",
  solutionHighlights: [
    "Implemented a high-resolution computer vision system to automate the detection of defects and anomalies on production lines.",
    "Utilized machine learning models trained on extensive datasets to identify subtle defects with greater accuracy than manual inspection.",
    "Integrated the system with production line controls for real-time alerts and automated sorting of defective items.",
    "Provided a dashboard for monitoring inspection results, tracking defect rates, and generating quality reports."
  ],
  benefits: [
    "Improved defect detection accuracy by 95%",
    "Reduced inspection time per unit by 70%",
    "Lowered instances of false positives/negatives significantly",
    "Enabled 24/7 automated quality monitoring"
  ],
  techStack: ["Computer Vision", "Machine Learning", "OpenCV", "Python", "TensorFlow", "Industrial Automation", "Edge AI"],
  qcStats: {
    totalInspected: "1,250",
    defectsFound: "3",
    warnings: "2",
    accuracy: "99.7%"
  }
};

export default function VisionQualityControlPage() {
  const params = useParams();
  const currentLocale = (params?.locale as string) || 'en';
  
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      {/* Header Section */}
      <header className={`py-16 md:py-24 text-white ${visionQCData.headerGradient}`}>
        <div className="container mx-auto max-w-4xl px-4 text-center">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <Link href={`/${currentLocale}/portfolio`} className="inline-flex items-center text-sm text-white hover:text-cyan-200 transition-colors duration-300 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full shadow-md">
              <ArrowLeft size={18} className="mr-2" />
              Back to Portfolio
            </Link>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            {visionQCData.title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-lg md:text-xl text-cyan-100 max-w-3xl mx-auto">
            {visionQCData.subtitle}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-8 flex flex-wrap gap-3 justify-center">
            {visionQCData.techStack.slice(0, 3).map(tech => (
              <span key={tech} className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-medium">
                {tech}
              </span>
            ))}
          </motion.div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="container mx-auto max-w-5xl px-4 py-12 md:py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white p-8 md:p-12 rounded-xl shadow-xl -mt-20 md:-mt-32 relative z-10">
          
          {/* QC Dashboard Visual Section */}
          <section className="mb-12">
            <h2 className={`text-3xl font-semibold mb-8 text-center ${visionQCData.accentColor}`}>Quality Control Dashboard</h2>
            <div className="bg-slate-100 p-4 sm:p-6 rounded-lg shadow-lg border border-slate-200">
              <div className="flex items-center justify-between px-4 py-2 mb-4 bg-gradient-to-r from-slate-700 to-slate-800 text-white text-sm font-semibold rounded-t-md shadow">
                <span>Visual Inspection Monitor</span>
                <span className="text-xs bg-green-500/90 px-2 py-0.5 rounded-full flex items-center"><Eye size={12} className="mr-1"/>Active</span>
              </div>
              <div className="flex flex-col md:flex-row gap-4 min-h-[280px]">
                {/* Simulated Product Frame - Light Theme */}
                <div className="flex-1 flex items-center justify-center bg-slate-200 rounded-md p-4 relative min-h-[200px]">
                  <div className="w-40 h-40 sm:w-48 sm:h-48 bg-white rounded-lg relative overflow-hidden shadow-inner border border-slate-300 flex items-center justify-center">
                    <Target size={64} className="text-slate-400 opacity-50" />
                    {/* Defect bounding boxes */}
                    <div className="absolute border-2 border-red-500 rounded-sm left-5 top-8 w-16 h-10 animate-pulse">
                      <span className="absolute -top-5 left-0 text-xs bg-red-500 text-white px-1.5 py-0.5 rounded shadow">Defect</span>
                    </div>
                    <div className="absolute border-2 border-yellow-400 rounded-sm right-4 bottom-6 w-12 h-8 animate-pulse">
                      <span className="absolute -bottom-5 left-0 text-xs bg-yellow-400 text-slate-800 px-1.5 py-0.5 rounded shadow">Warning</span>
                    </div>
                  </div>
                </div>
                {/* Sidebar Stats - Light Theme */}
                <div className="w-full md:w-48 bg-slate-50 rounded-md p-4 space-y-2.5 ring-1 ring-slate-200 flex flex-col justify-center">
                  {[ 
                    { label: "Total Inspected", value: visionQCData.qcStats.totalInspected, color: "text-slate-700", bgColor:"bg-slate-200" }, 
                    { label: "Defects Found", value: visionQCData.qcStats.defectsFound, color: "text-red-700", bgColor:"bg-red-100" }, 
                    { label: "Warnings Issued", value: visionQCData.qcStats.warnings, color: "text-yellow-700", bgColor:"bg-yellow-100" }, 
                    { label: "Overall Accuracy", value: visionQCData.qcStats.accuracy, color: "text-green-700", bgColor:"bg-green-100" } 
                  ].map(stat => (
                    <div key={stat.label} className={`flex items-center justify-between p-2 rounded ${stat.bgColor}`}>
                      <span className={`text-xs font-medium ${stat.color}`}>{stat.label}</span>
                      <span className={`font-bold text-sm ${stat.color}`}>{stat.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-4 py-1.5 mt-3 bg-slate-200 border-t border-slate-300 text-xs text-slate-600 flex items-center gap-2 rounded-b-md">
                <span className="w-2 h-2 rounded-full bg-green-500 inline-block animate-pulse"></span>
                <span>System Operational. Last scan: 2 seconds ago.</span>
              </div>
            </div>
          </section>

          <div className="grid md:grid-cols-1 gap-10">
            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${visionQCData.accentColor}`}>The Challenge</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-line">
                {visionQCData.challenge}
              </p>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${visionQCData.accentColor}`}>Our Solution</h2>
              <ul className="space-y-3 text-slate-700">
                {visionQCData.solutionHighlights.map((item, index) => (
                  <motion.li key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }} className="flex items-start pl-2">
                    <CheckCircle className={`w-5 h-5 ${visionQCData.accentColor} mr-3 mt-0.5 flex-shrink-0`} />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${visionQCData.accentColor}`}>Key Benefits</h2>
              <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg shadow-sm">
                <ul className="space-y-3">
                  {visionQCData.benefits.map((benefit, index) => (
                    <motion.li key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }} className={`flex items-start text-blue-800`}>
                      <CheckCircle className={`w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0`} />
                      <span>{benefit}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-5 ${visionQCData.accentColor}`}>Technologies Used</h2>
              <div className="flex flex-wrap gap-3">
                {visionQCData.techStack.map((tech, index) => (
                  <motion.span key={index} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: index * 0.05 }}
                    className="bg-slate-200 text-slate-700 px-4 py-1.5 rounded-full text-sm font-medium border border-slate-300 hover:bg-slate-300 transition-colors cursor-default">
                    {tech}
                  </motion.span>
                ))}
              </div>
            </section>
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-16 text-center">
            <Link href={`/${currentLocale}/contact`} className={`${visionQCData.buttonColor} text-white font-semibold py-3.5 px-10 rounded-lg text-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50`}>
              Enhance Your Quality Control
            </Link>
          </motion.div>

        </motion.div>
      </main>
    </div>
  );
} 